import React, { useState, useEffect } from 'react';
import Snackbar from './Snackbar';
import ProductRateManagement from './ProductRateManagement';
import AdminUserManagement from './AdminUserManagement';
import './Settings.css';
import '../styles/analytics-theme.css';

const Settings = ({ user, onLogout, onShowNotification }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('info');
  
  // Default settings
  const defaultSettings = {
    profile: {
      username: user?.username || 'admin',
      email: 'admin@bimabot.com',
      fullName: 'Administrator',
      phone: '+254 700 000 000',
      role: 'Administrator'
    },
    preferences: {
      theme: 'light',
      language: 'en',
      timezone: 'Africa/Nairobi',
      notifications: {
        email: true,
        push: true,
        sms: false
      },
      dashboard: {
        defaultView: 'overview',
        itemsPerPage: 10,
        autoRefresh: true,
        refreshInterval: 30
      }
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: 30,
      passwordExpiry: 90,
      loginAttempts: 5
    }
  };

  const [settings, setSettings] = useState(defaultSettings);

  // Load settings from localStorage on component mount
  useEffect(() => {
    const loadSettings = () => {
      try {
        const savedSettings = localStorage.getItem('bimabot_admin_settings');
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          setSettings(prev => ({
            ...prev,
            ...parsedSettings,
            profile: {
              ...prev.profile,
              ...parsedSettings.profile,
              username: prev.profile.username, // Keep username immutable
              role: prev.profile.role // Keep role immutable
            }
          }));
        }
      } catch (error) {
        if (onShowNotification) {
          onShowNotification('Failed to load saved settings. Using defaults.', 'warning');
        }
      }
    };

    loadSettings();
  }, [onShowNotification]);

  const showNotification = (message, type = 'info') => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setShowSnackbar(true);
  };

  // Apply settings changes to the application
  const applySettings = (newSettings) => {
    // Apply theme changes
    if (newSettings.preferences?.theme) {
      document.documentElement.setAttribute('data-theme', newSettings.preferences.theme);
    }

    // Apply language changes
    if (newSettings.preferences?.language) {
      document.documentElement.setAttribute('lang', newSettings.preferences.language);
    }

    // Store other preferences for use by other components
    localStorage.setItem('bimabot_admin_preferences', JSON.stringify(newSettings.preferences));
  };

  const handleInputChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNestedInputChange = (section, parentField, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [parentField]: {
          ...prev[section][parentField],
          [field]: value
        }
      }
    }));
  };

  const handleSave = async (section) => {
    setIsLoading(true);
    try {
      // Save to localStorage
      localStorage.setItem('bimabot_admin_settings', JSON.stringify(settings));
      
      // Apply settings changes immediately
      applySettings(settings);
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Show success notification
      const sectionNames = {
        profile: 'Profile',
        preferences: 'Preferences',
        security: 'Security',
        notifications: 'Notification'
      };
      
      showNotification(`${sectionNames[section] || section} settings saved successfully!`, 'success');
      
      // Also show notification via parent if available
      if (onShowNotification) {
        onShowNotification(`${sectionNames[section] || section} settings saved successfully!`, 'success');
      }
    } catch (error) {
      showNotification('Failed to save settings. Please try again.', 'error');
      if (onShowNotification) {
        onShowNotification('Failed to save settings. Please try again.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = () => {
    showNotification('Password change functionality will be available soon.', 'info');
    if (onShowNotification) {
      onShowNotification('Password change functionality will be available soon.', 'info');
    }
  };

  const handleResetSessions = () => {
    if (window.confirm('Are you sure you want to reset all active sessions? This will log out all devices.')) {
      showNotification('All sessions have been reset successfully.', 'success');
      if (onShowNotification) {
        onShowNotification('All sessions have been reset successfully.', 'success');
      }
    }
  };

  const handleLogout = () => {
    onLogout();
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: '👤' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    ...(user?.role === 'super_admin' ? [
      { id: 'product-rates', label: 'Product Rates', icon: '💰' },
      { id: 'admin-users', label: 'Admin Users', icon: '👥' }
    ] : [])
  ];

  return (
    <div className="settings">
      <div className="settings-container">
        {/* Settings Header */}
        <div className="settings-header">
          <div className="header-content">
            <h2>Settings</h2>
            <p>Manage your account and application preferences</p>
          </div>
        </div>

        <div className="settings-content">
          {/* Settings Sidebar */}
          <div className="settings-sidebar">
            <nav className="settings-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="nav-icon">{tab.icon}</span>
                  <span className="nav-label">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Settings Main Content */}
          <div className="settings-main">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="settings-section">
                <div className="section-header">
                  <h3>Profile Information</h3>
                  <p>Update your personal information and contact details</p>
                </div>
                
                <div className="profile-form">
                  <div className="form-group">
                    <label>Username</label>
                    <input
                      type="text"
                      value={settings.profile.username}
                      onChange={(e) => handleInputChange('profile', 'username', e.target.value)}
                      disabled
                    />
                    <small>Username cannot be changed</small>
                  </div>

                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={settings.profile.fullName}
                      onChange={(e) => handleInputChange('profile', 'fullName', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      value={settings.profile.email}
                      onChange={(e) => handleInputChange('profile', 'email', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={settings.profile.phone}
                      onChange={(e) => handleInputChange('profile', 'phone', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Role</label>
                    <input
                      type="text"
                      value={settings.profile.role}
                      disabled
                    />
                    <small>Role is assigned by system administrator</small>
                  </div>

                  <div className="form-actions">
                    <button 
                      className="save-btn"
                      onClick={() => handleSave('profile')}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="settings-section">
                <div className="section-header">
                  <h3>Application Preferences</h3>
                  <p>Customize your dashboard and application behavior</p>
                </div>
                
                <div className="preferences-form">
                  <div className="preference-group">
                    <h4>Appearance</h4>
                    <div className="form-group">
                      <label>Theme</label>
                      <select
                        value={settings.preferences.theme}
                        onChange={(e) => handleInputChange('preferences', 'theme', e.target.value)}
                      >
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                        <option value="auto">Auto</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Language</label>
                      <select
                        value={settings.preferences.language}
                        onChange={(e) => handleInputChange('preferences', 'language', e.target.value)}
                      >
                        <option value="en">English</option>
                        <option value="sw">Swahili</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Timezone</label>
                      <select
                        value={settings.preferences.timezone}
                        onChange={(e) => handleInputChange('preferences', 'timezone', e.target.value)}
                      >
                        <option value="Africa/Nairobi">Nairobi (EAT)</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>
                  </div>

                  <div className="preference-group">
                    <h4>Dashboard</h4>
                    <div className="form-group">
                      <label>Default View</label>
                      <select
                        value={settings.preferences.dashboard.defaultView}
                        onChange={(e) => handleNestedInputChange('preferences', 'dashboard', 'defaultView', e.target.value)}
                      >
                        <option value="overview">Overview</option>
                        <option value="policies">Policies</option>
                        <option value="revenue">Revenue</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Items per Page</label>
                      <select
                        value={settings.preferences.dashboard.itemsPerPage}
                        onChange={(e) => handleNestedInputChange('preferences', 'dashboard', 'itemsPerPage', parseInt(e.target.value))}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={settings.preferences.dashboard.autoRefresh}
                          onChange={(e) => handleNestedInputChange('preferences', 'dashboard', 'autoRefresh', e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        Auto Refresh Dashboard
                      </label>
                    </div>

                    <div className="form-group">
                      <label>Refresh Interval (seconds)</label>
                      <input
                        type="number"
                        value={settings.preferences.dashboard.refreshInterval}
                        onChange={(e) => handleNestedInputChange('preferences', 'dashboard', 'refreshInterval', parseInt(e.target.value))}
                        min="10"
                        max="300"
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      className="save-btn"
                      onClick={() => handleSave('preferences')}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save Preferences'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="settings-section">
                <div className="section-header">
                  <h3>Security Settings</h3>
                  <p>Manage your account security and authentication</p>
                </div>
                
                <div className="security-form">
                  <div className="security-group">
                    <h4>Authentication</h4>
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={settings.security.twoFactorAuth}
                          onChange={(e) => handleInputChange('security', 'twoFactorAuth', e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        Enable Two-Factor Authentication
                      </label>
                      <small>Add an extra layer of security to your account</small>
                    </div>

                    <div className="form-group">
                      <label>Session Timeout (minutes)</label>
                      <select
                        value={settings.security.sessionTimeout}
                        onChange={(e) => handleInputChange('security', 'sessionTimeout', parseInt(e.target.value))}
                      >
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={60}>1 hour</option>
                        <option value={120}>2 hours</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Password Expiry (days)</label>
                      <select
                        value={settings.security.passwordExpiry}
                        onChange={(e) => handleInputChange('security', 'passwordExpiry', parseInt(e.target.value))}
                      >
                        <option value={30}>30 days</option>
                        <option value={60}>60 days</option>
                        <option value={90}>90 days</option>
                        <option value={180}>180 days</option>
                        <option value={365}>1 year</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Max Login Attempts</label>
                      <select
                        value={settings.security.loginAttempts}
                        onChange={(e) => handleInputChange('security', 'loginAttempts', parseInt(e.target.value))}
                      >
                        <option value={3}>3 attempts</option>
                        <option value={5}>5 attempts</option>
                        <option value={10}>10 attempts</option>
                      </select>
                    </div>
                  </div>

                  <div className="security-group">
                    <h4>Password Management</h4>
                    <div className="password-actions">
                      <button 
                        className="action-btn change-password"
                        onClick={handleChangePassword}
                      >
                        🔑 Change Password
                      </button>
                      <button 
                        className="action-btn reset-sessions"
                        onClick={handleResetSessions}
                      >
                        🔄 Reset All Sessions
                      </button>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      className="save-btn"
                      onClick={() => handleSave('security')}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save Security Settings'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="settings-section">
                <div className="section-header">
                  <h3>Notification Preferences</h3>
                  <p>Choose how you want to be notified about important events</p>
                </div>
                
                <div className="notifications-form">
                  <div className="notification-group">
                    <h4>Notification Channels</h4>
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={settings.preferences.notifications.email}
                          onChange={(e) => handleNestedInputChange('preferences', 'notifications', 'email', e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        Email Notifications
                      </label>
                      <small>Receive notifications via email</small>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={settings.preferences.notifications.push}
                          onChange={(e) => handleNestedInputChange('preferences', 'notifications', 'push', e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        Push Notifications
                      </label>
                      <small>Receive browser push notifications</small>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={settings.preferences.notifications.sms}
                          onChange={(e) => handleNestedInputChange('preferences', 'notifications', 'sms', e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        SMS Notifications
                      </label>
                      <small>Receive notifications via SMS</small>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      className="save-btn"
                      onClick={() => handleSave('notifications')}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save Notification Settings'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Product Rate Management Tab (Super Admin only) */}
            {activeTab === 'product-rates' && user?.role === 'super_admin' && (
              <div className="settings-section">
                <ProductRateManagement user={user} onShowNotification={onShowNotification} />
              </div>
            )}

            {/* Admin User Management Tab (Super Admin only) */}
            {activeTab === 'admin-users' && user?.role === 'super_admin' && (
              <div className="settings-section">
                <AdminUserManagement user={user} onShowNotification={onShowNotification} />
              </div>
            )}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="danger-zone">
          <h3>Danger Zone</h3>
          <p>Irreversible and destructive actions</p>
          <div className="danger-actions">
            <button className="danger-btn logout-btn" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
        </div>
      </div>
      
      {/* Snackbar Notifications */}
      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
          duration={5000}
        />
      )}
    </div>
  );
};

export default Settings;
